import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2 } from 'lucide-react';
import { SlidePackagesTableProps } from '../types';

export function SlidePackagesTable({
  isDark,
  packages,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
  formatPrice,
}: SlidePackagesTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow
          className={cn(
            isDark
              ? 'border-slate-700 hover:bg-slate-800/50'
              : 'border-slate-100 hover:bg-slate-50',
          )}
        >
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Code</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Name (UZ / RU)</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Decks</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Price</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Order</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Status</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {packages.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-12 text-slate-500">
              No packages found
            </TableCell>
          </TableRow>
        ) : (
          packages.map((pkg) => (
            <TableRow
              key={pkg.id}
              className={cn(
                'transition-colors',
                isDark
                  ? 'border-slate-700 hover:bg-slate-800/50'
                  : 'border-slate-100 hover:bg-slate-50',
                !pkg.is_active && 'opacity-60',
              )}
            >
              <TableCell className={cn('font-mono text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>{pkg.code}</TableCell>
              <TableCell>
                <div>
                  <p className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>{pkg.name_uz}</p>
                  <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{pkg.name_ru}</p>
                </div>
              </TableCell>
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>{pkg.decks_included}</TableCell>
              <TableCell className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>{formatPrice(pkg.price_uzs)}</TableCell>
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>{pkg.display_order}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Switch checked={pkg.is_active} onCheckedChange={() => onToggleActive(pkg)} />
                  <span
                    className={cn(
                      'text-xs font-medium',
                      pkg.is_active ? 'text-emerald-500' : isDark ? 'text-slate-500' : 'text-slate-400',
                    )}
                  >
                    {pkg.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(pkg)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100',
                    )}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(pkg.id)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400' : 'text-slate-600 hover:bg-slate-100 hover:text-red-600',
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
